import {computed, ref, toValue, watch, type MaybeRefOrGetter, type Ref} from 'vue';

interface SavedSmsConsent {
    phone: MaybeRefOrGetter<string | undefined>;
    savedPhoneNumber: MaybeRefOrGetter<string | undefined>;
    isTransactionalSmsAllowed: Ref<boolean | undefined>;
    savedIsTransactionalSmsAllowed: MaybeRefOrGetter<boolean | undefined>;
}

export function useSmsConsent(smsConsentCountries: Ref<string[] | undefined>, savedSmsConsent?: SavedSmsConsent) {
    const phoneCountryCode = ref('');

    const isSmsConsentShown = computed(() => smsConsentCountries.value?.includes(phoneCountryCode.value) ?? false);

    // PhoneNumber reformats the number and the backend formats US numbers with dashes: compare digits only
    const hasPhoneNumberChanged = computed(() => digitsOf(toValue(savedSmsConsent?.phone)) !== digitsOf(toValue(savedSmsConsent?.savedPhoneNumber)));

    // Compliance rule: a saved consent only holds for the saved number
    if (savedSmsConsent) {
        watch(hasPhoneNumberChanged, (changed) => {
            savedSmsConsent.isTransactionalSmsAllowed.value = changed ? false : toValue(savedSmsConsent.savedIsTransactionalSmsAllowed);
        });
    }

    function handleCountryChanged(countryCode: string) {
        phoneCountryCode.value = countryCode;
    }

    function smsConsentToSave(isTransactionalSmsAllowed: boolean | undefined) {
        return isSmsConsentShown.value ? (isTransactionalSmsAllowed ?? false) : undefined;
    }

    return {
        isSmsConsentShown,
        hasPhoneNumberChanged,
        handleCountryChanged,
        smsConsentToSave,
    };
}

function digitsOf(phoneNumber: string | undefined) {
    return phoneNumber?.replace(/\D/g, '') ?? '';
}
